import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApis } from '../contexts/ApiContext';
import { 
  FaHome, 
  FaPlus, 
  FaSearch, 
  FaFileAlt, 
  FaShoppingCart,
  FaEye,
  FaEdit,
  FaTrash
} from 'react-icons/fa';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('Draft Orders');
  const [draftOrders, setDraftOrders] = useState([]);
  const [inProcessingOrders, setInProcessingOrders] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [activeOrdersTotal, setActiveOrdersTotal] = useState(0);
  const [completedOrdersTotal, setCompletedOrdersTotal] = useState(0);
  const [activeOrdersPage, setActiveOrdersPage] = useState(1);
  const [completedOrdersPage, setCompletedOrdersPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingEstimate, setCreatingEstimate] = useState(false);
  const [showCreateEstimateModal, setShowCreateEstimateModal] = useState(false);
  const [orderTag, setOrderTag] = useState('');

  const { user, setUser } = useAuth();
  const { estimateApis, userApis, orderApis } = useApis();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    if (!estimateApis || !userApis || !orderApis) {
      console.log('APIs not ready:', { estimateApis: !!estimateApis, userApis: !!userApis, orderApis: !!orderApis });
      return;
    }

    console.log('User data:', user);
    console.log('Fetching data with APIs:', { estimateApis, userApis });

    try {
      setLoading(true);
      
      console.log('Making API calls...');
      
      // // Step 1: Clean up orphaned estimates first
      // console.log('🧹 Dashboard: Running cleanup of orphaned estimates...');
      // try {
      //   const orphanedCount = await estimateApis.cleanupOrphanedEstimates();
      //   if (orphanedCount.length > 0) {
      //     console.log(`✅ Dashboard: Cleaned up ${orphanedCount.length} orphaned estimates`);
      //   }
      // } catch (cleanupError) {
      //   console.log('⚠️ Dashboard: Cleanup failed, but continuing with data fetch:', cleanupError);
      // }
      
      // Step 2: Fetch current data for all 4 order types
      const [storeData, draftEstimatesData, inProcessingOrdersData, activeOrdersData, completedOrdersData] = await Promise.all([
        userApis.getStore().then(data => {
          console.log('Store data received:', data);
          return data;
        }),
        estimateApis.getStoreEstimates("draft").then(data => {
          console.log('Draft estimates data received:', data);
          return data;
        }),
        orderApis.getInProcessingOrders().then(data => {
          console.log('In processing orders data received:', data);
          return data;
        }),
        orderApis.getActiveOrders(1, 10).then(data => {
          console.log('Active orders data received:', data);
          return data;
        }),
        orderApis.getCompletedOrders(1, 10).then(data => {
          console.log('Completed orders data received:', data);
          return data;
        })
      ]);

      console.log('Final data:', { storeData, draftEstimatesData, inProcessingOrdersData, activeOrdersData, completedOrdersData });

      setStore(storeData);
      user.storeData = storeData;
      setUser(user);
      setDraftOrders(draftEstimatesData || []);
      setInProcessingOrders(inProcessingOrdersData || []);
      setActiveOrders(activeOrdersData?.orders || []);
      setCompletedOrders(completedOrdersData?.orders || []);
      setActiveOrdersTotal(activeOrdersData?.total || 0);
      setCompletedOrdersTotal(completedOrdersData?.total || 0);
      setActiveOrdersPage(1);
      setCompletedOrdersPage(1);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Show user-friendly error
      alert(`Error loading data: ${error.message}. Please check the console for details.`);
    } finally {
      setLoading(false);
    }
  }, [estimateApis, userApis, orderApis, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load more active orders
  const loadMoreActiveOrders = async () => {
    try {
      setLoading(true);
      const nextPage = activeOrdersPage + 1;
      const newData = await orderApis.getActiveOrders(nextPage, 10);
      setActiveOrders(prev => [...prev, ...(newData?.orders || [])]);
      setActiveOrdersPage(nextPage);
    } catch (error) {
      console.error('Error loading more active orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load more completed orders
  const loadMoreCompletedOrders = async () => {
    try {
      setLoading(true);
      const nextPage = completedOrdersPage + 1;
      const newData = await orderApis.getCompletedOrders(nextPage, 10);
      setCompletedOrders(prev => [...prev, ...(newData?.orders || [])]);
      setCompletedOrdersPage(nextPage);
    } catch (error) {
      console.error('Error loading more completed orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = ['Draft Orders', 'In Processing', 'Active Orders', 'Completed Orders'];

  const filteredData = () => {
    let data = [];
    switch (activeTab) {
      case 'Draft Orders':
        data = draftOrders; // Firebase estimates with status "draft"
        break;
      case 'In Processing':
        data = inProcessingOrders; // Circle API order carts
        break;
      case 'Active Orders':
        data = activeOrders; // Circle API orders with status "active"
        break;
      case 'Completed Orders':
        data = completedOrders; // Circle API orders with status "completed"
        break;
      default:
        data = draftOrders;
    }

    if (searchQuery) {
      data = data.filter(item => {
        const query = searchQuery.toLowerCase();
        return (
          // For estimates (Draft Orders)
          item.customerName?.toLowerCase().includes(query) ||
          item.estimateNo?.toString().includes(query) ||
          // For Circle API orders (other tabs)
          item.tag?.toLowerCase().includes(query) ||
          item.orderNumber?.toLowerCase().includes(query) ||
          // ID
          item.id?.toString().includes(query) ||
          // Status
          item.status?.toLowerCase().includes(query)
        );
      });
    }

    return data;
  };

  const handleViewOrder = (orderId, orderType) => {
    // Navigate to appropriate detail page based on order type
    if (orderType === 'customer_cart' || orderType === 'order_cart') {
      navigate(`/cart-details/${orderId}`);
    } else {
      navigate(`/order-details/${orderId}`);
    }
  };

  const handleCreateEstimate = async () => {
    if (!orderTag.trim()) {
      alert('Please enter an order tag');
      return;
    }

    if (creatingEstimate) return; // Prevent double submissions

    try {
      setCreatingEstimate(true);
      setShowCreateEstimateModal(false);
      navigate('/create-estimate', {
        state: {
          orderTag: orderTag.trim()
        }
      });
    } catch (error) {
      console.error('Error creating estimate:', error);
      alert('Error creating estimate: ' + error.message);
    } finally {
      setCreatingEstimate(false);
    }
  };

  const handleCleanupOrphaned = async () => {
    if (!estimateApis) return;
    
    const confirmed = window.confirm(
      'This will remove estimates from your dashboard that no longer exist in the system. ' +
      'This action cannot be undone. Continue?'
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      console.log('🧹 Dashboard: Manual cleanup triggered...');
      const orphanedEstimates = await estimateApis.cleanupOrphanedEstimates();
      
      if (orphanedEstimates.length > 0) {
        alert(`Cleanup completed! Removed ${orphanedEstimates.length} orphaned estimates.`);
        // Refresh the data
        await fetchData();
      } else {
        alert('No orphaned estimates found. Your data is already synchronized.');
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
      alert('Error during cleanup: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Calculate total price from estimate data
  const calculateTotalPrice = (item) => {
    // Try multiple sources for the total price
    const estimateData = item.estimateData || {};
    
    // First check direct total_price in estimateData
    if (estimateData.total_price && estimateData.total_price > 0) {
      return estimateData.total_price;
    }
    
    // Then try to sum up individual product prices
    if (estimateData.products && estimateData.products.length > 0) {
      const productTotal = estimateData.products.reduce((total, product) => {
        const productPrice = parseFloat(
          product.customer_item_total || 
          product.item_total || 
          product.customerItemTotal ||
          product.itemTotal ||
          product.product_price ||
          product.customer_product_price ||
          0
        );
        return total + productPrice;
      }, 0);
      
      if (productTotal > 0) {
        return productTotal;
      }
    }
    
    // Fallback to any price field in the main item
    return parseFloat(
      item.total_price ||
      item.customer_item_total ||
      item.item_total ||
      0
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-6">
          <div className="flex items-center space-x-3 mb-2">
            <FaHome className="text-blue-500 text-xl" />
            <h1 className="text-2xl font-bold text-gray-900">
              {store?.store_name} Dealer
            </h1>
          </div>
          <p className="text-gray-600">
            Today is {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-0 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {/* Content Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-3 sm:space-y-0">
          <h2 className="text-xl font-semibold text-gray-900">{activeTab}</h2>
          
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Add Button */}
            {activeTab === 'Draft Orders' && (
              <button
                onClick={() => setShowCreateEstimateModal(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:ring-4 focus:ring-blue-200 transition-all duration-200 flex items-center space-x-2"
              >
                <FaPlus className="text-sm" />
                <span>New Order</span>
              </button>
            )}
            
            {/* Cleanup Button */}
            <button
              onClick={handleCleanupOrphaned}
              disabled={loading}
              className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 focus:ring-4 focus:ring-orange-200 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
              title="Clean up estimates that no longer exist in the system"
            >
              <span>🧹</span>
              <span>Cleanup</span>
            </button>
          </div>
        </div>

        {/* Content List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {filteredData().length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                {activeTab === 'Draft Orders' && <FaFileAlt className="w-8 h-8 text-gray-400" />}
                {activeTab === 'In Processing' && <FaShoppingCart className="w-8 h-8 text-gray-400" />}
                {activeTab === 'Active Orders' && <FaShoppingCart className="w-8 h-8 text-gray-400" />}
                {activeTab === 'Completed Orders' && <FaShoppingCart className="w-8 h-8 text-gray-400" />}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeTab} Found</h3>
              <p className="text-gray-500 mb-4">
                {activeTab === 'Draft Orders' 
                  ? 'Create your first draft order to get started'
                  : `You don't have any ${activeTab.toLowerCase()} yet`
                }
              </p>
              {activeTab !== 'Draft Orders' && (
                <p className="text-xs text-gray-400 mb-4">
                  Note: Detailed views for {activeTab.toLowerCase()} are currently in preview mode
                </p>
              )}
              {activeTab === 'Draft Orders' && (
                <button
                  onClick={() => setShowCreateEstimateModal(true)}
                  className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                >
                  Create First Order
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredData().map((item) => (
                <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {activeTab === 'Draft Orders' 
                            ? `Estimate #${item.estimateNo || item.id}`
                            : item.type === 'order' ? `Order #${item.orderNumber || item.id}` 
                            : item.type === 'order_cart' ? `Order Cart #${item.id}`
                            : `Cart #${item.id}`
                          }
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.status === 'draft' 
                            ? 'bg-yellow-100 text-yellow-800'
                                              : item.status === 'Active'
                  ? 'bg-green-100 text-green-800'
                  : item.status === 'Completed'
                            ? 'bg-gray-100 text-gray-800'
                            : item.status === 'Processing'
                            ? 'bg-blue-100 text-blue-800'
                            : item.isConvertedToOrder
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {activeTab === 'Draft Orders' 
                            ? (item.status === 'draft' ? 'Draft' : item.isConvertedToOrder ? 'Active' : 'Saved')
                            : (item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || 'Unknown')
                          }
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        {activeTab === 'Draft Orders' || activeTab === 'In Processing' ? (
                          <>
                              {item.customerName && <p><span className="font-medium">Tag:</span> {item.customerName}</p>}
                            {item.tag && <p><span className="font-medium">Tag:</span> {item.tag}</p>}
                            <p><span className="font-medium">Created:</span> {formatDate(item.createdAt)}</p>
                          </>
                        ) : (
                          <>
                            <p><span className="font-medium">Amount: ${item.totalAmount?.toFixed(2) || '0.00'}</span> </p>
                            <p><span className="font-medium">Created:</span> {formatDate(item.createdAt)}</p>
                            {item.tag && <p><span className="font-medium">Tag:</span> {item.tag}</p>}
                            {item.completedAt && <p><span className="font-medium">Completed:</span> {formatDate(item.completedAt)}</p>}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {activeTab === 'Draft Orders' ? (
                        <>
                          <button
                            onClick={() => navigate(`/estimate-details/${item.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                            title="View Estimate"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/edit-estimate/${item.id}`)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                            title="Edit Estimate"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this order?')) {
                            estimateApis.deleteEstimate(item.id).then(() => {
                              fetchData();
                            });
                          }
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleViewOrder(item.id, item.type)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                          title={`View ${item.type === 'order' ? 'Order' : 'Cart'} Details (Preview)`}
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                      )}
  
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Load More Button for Active Orders */}
              {activeTab === 'Active Orders' && activeOrders.length < activeOrdersTotal && (
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={loadMoreActiveOrders}
                    disabled={loading}
                    className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {loading ? 'Loading...' : `Load More (${activeOrders.length} of ${activeOrdersTotal})`}
                  </button>
                </div>
              )}
              
              {/* Load More Button for Completed Orders */}
              {activeTab === 'Completed Orders' && completedOrders.length < completedOrdersTotal && (
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={loadMoreCompletedOrders}
                    disabled={loading}
                    className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {loading ? 'Loading...' : `Load More (${completedOrders.length} of ${completedOrdersTotal})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Estimate Modal */}
      {showCreateEstimateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Create New Order</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Tag
                  </label>
                  <input
                    type="text"
                    value={orderTag}
                    onChange={(e) => setOrderTag(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter order tag"
                  />
                </div>
                
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleCreateEstimate}
                  disabled={creatingEstimate}
                  className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingEstimate ? 'Creating Order...' : 'Create Order'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateEstimateModal(false);
                    setOrderTag('');
                  }}
                  disabled={creatingEstimate}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 